import { lazy } from "react";
import { Link } from "react-router-dom";
import {
  Camera, Sparkles, Pickaxe, ChevronRight, Mountain, Shield, Zap,
  Building, Users, GraduationCap, HardHat, BarChart3, Gem,
} from "lucide-react";
import { CriticalButton as Button } from "@/components/layout/CriticalButton";

import { StatsBar } from "@/components/home/StatsBar";
import { SectionDivider } from "@/components/ui/section-divider";
import { DeferredHomeSection } from "@/components/home/DeferredHomeSection";
import { usePageSEO } from "@/hooks/usePageSEO";
import { popularGuides } from "@/data/popularGuides";
import { trackEvent } from "@/lib/analytics";
import { SavedReferenceShelf } from "@/components/reference/SavedReferenceShelf";
import { ReturningMemberHub } from "@/components/home/ReturningMemberHub";
import { SpecimenStarter } from "@/components/home/SpecimenStarter";
import { useAuth } from "@/contexts/AuthContext";
import { useGlobalStats } from "@/hooks/useUsage";
import { publicCountLabel } from "@/lib/publicPlatformStats";
import { RecentGuideShelf } from "@/components/guides/RecentGuideShelf";

const AudienceCards = lazy(() => import("@/components/home/AudienceCards").then((module) => ({ default: module.AudienceCards })));
const FeatureGrid = lazy(() => import("@/components/home/FeatureGrid").then((module) => ({ default: module.FeatureGrid })));
const ProToolsDeepDive = lazy(() => import("@/components/home/ProToolsDeepDive").then((module) => ({ default: module.ProToolsDeepDive })));
const TrainingCareers = lazy(() => import("@/components/home/TrainingCareers").then((module) => ({ default: module.TrainingCareers })));
const MineralOfTheDay = lazy(() => import("@/components/home/MineralOfTheDay").then((module) => ({ default: module.MineralOfTheDay })));
const DailyFunFact = lazy(() => import("@/components/home/DailyFunFact").then((module) => ({ default: module.DailyFunFact })));
const DailyFieldChallenge = lazy(() => import("@/components/home/DailyFieldChallenge").then((module) => ({ default: module.DailyFieldChallenge })));
const RockLearningPath = lazy(() => import("@/components/home/RockLearningPath").then((module) => ({ default: module.RockLearningPath })));

export default function HomePage() {
  const { user } = useAuth();
  const { data: platformStats } = useGlobalStats();
  const lessonProof = publicCountLabel(
    platformStats?.free_lessons,
    "published lesson",
    "published lessons",
    "Free geology lessons",
  );
  const referenceProof = publicCountLabel(
    platformStats?.reference_guides,
    "reference item",
    "reference items",
    "Source-backed reference",
  );
  const careerProof = publicCountLabel(
    platformStats?.career_profiles,
    "career profile",
    "career profiles",
    "Mining career guides",
  );
  const trainingTrackProof = publicCountLabel(
    platformStats?.training_tracks,
    "published track",
    "published tracks",
    "Structured courses",
  );
  usePageSEO({
    title: "GeoMiner — AI Rock & Mineral Identification App",
    description: "Upload a specimen photo for rock, mineral, or fossil identification candidates. Free geology field tools, training, and a community built by a geologist.",
  });
  return (
    <div className="min-h-screen">
      <div>



      {/* 1. Hero */}
      <section className="hero-gradient text-white relative overflow-hidden noise-overlay">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(255,255,255,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.05),transparent_40%)]" />
        </div>

        <div className="container relative py-8 md:py-24 lg:py-32">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm">
                <Mountain className="h-4 w-4" />
                <span>Built by a Geologist</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight">
                Identify Rocks & Minerals
                <br />
                <span className="gradient-text">Like a Pro</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
                AI-assisted identification, practical field tools, and
                structured training — all in one platform designed for
                geologists, students, and enthusiasts.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="accent" className="w-full sm:w-auto touch-button">
                <Link to="/quick-id" onClick={() => trackEvent("home_cta", { cta: "/quick-id", placement: "hero" })}>
                  <Gem className="h-5 w-5" />
                  Start Quick ID — No Account
                </Link>
              </Button>
              <Button asChild size="lg" variant="secondary" className="gap-2 w-full sm:w-auto touch-button">
                <Link to="/identify" onClick={() => trackEvent("home_cta", { cta: "/identify", placement: "hero" })}>
                  <Camera className="h-5 w-5" />
                  Identify from Photos
                </Link>
              </Button>
              <Button asChild size="lg" variant="hero-ghost" className="w-full sm:w-auto touch-button">
                <Link to="/careers/quiz" onClick={() => trackEvent("home_cta", { cta: "/careers/quiz", placement: "hero" })}>
                  <Sparkles className="h-5 w-5" />
                  Find My Career
                </Link>
              </Button>
            </div>

            <p className="text-sm font-medium text-white/75">
              Everything is free. Quick ID works instantly; visitors can also try one photo identification without an account. Create a free account for more IDs and saved finds—no credit card required.
            </p>

            {/* Trust badges with real numbers */}
            <div
              className="grid min-h-[6rem] grid-cols-1 gap-3 pt-4 text-sm text-white/70 sm:min-h-0 sm:grid-cols-3 sm:gap-6"
              data-home-proof-grid
            >
              <div className="flex min-w-0 items-center justify-center gap-2">
                <Sparkles className="h-4 w-4" />
                <span>{lessonProof}</span>
              </div>
              <div className="flex min-w-0 items-center justify-center gap-2">
                <Shield className="h-4 w-4" />
                <span>{referenceProof}</span>
              </div>
              <div className="flex min-w-0 items-center justify-center gap-2">
                <Zap className="h-4 w-4" />
                <span>{careerProof}</span>
              </div>
            </div>

            {/* Tagline */}
            <p className="pt-2 text-base md:text-lg text-white/60 font-medium italic">
              It's not rocket science. It's just rocks and science!
            </p>
          </div>
        </div>
      </section>

      {/* 2. Stats Bar */}
      <StatsBar stats={platformStats} />

      <div className="container space-y-6 pt-8 md:pt-12">
        <SpecimenStarter />
        <ReturningMemberHub />
        <SavedReferenceShelf placement="home" />
        <RecentGuideShelf placement="home" />
      </div>

      {/* Daily Content Cards */}
      <DeferredHomeSection>
        <section className="container py-8 md:py-12">
          <div className="text-center mb-6">
            <h2 className="text-lg md:text-xl font-display font-bold text-foreground">Today on GeoMiner</h2>
            <p className="text-sm text-muted-foreground mt-1">Build one field skill, discover one mineral, and learn one sourced fact</p>
          </div>
          <div className="mx-auto max-w-4xl space-y-4">
            <DailyFieldChallenge />
            <div className="grid min-w-0 gap-4 md:grid-cols-2 [&>*]:min-w-0">
              <MineralOfTheDay />
              <DailyFunFact />
            </div>
          </div>
        </section>
      </DeferredHomeSection>

      <SectionDivider />

      <DeferredHomeSection>
        <RockLearningPath />
      </DeferredHomeSection>

      <SectionDivider />

      {/* Popular field-identification pathways */}
      <section className="container py-10 md:py-14" aria-labelledby="popular-guides-heading">
        <div className="mx-auto max-w-5xl">
          <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-accent">Field-tested learning</p>
              <h2 id="popular-guides-heading" className="font-display text-2xl font-bold md:text-3xl">
                Popular rock, mineral & fossil identification guides
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">
                Start with the property that separates the candidates—not colour alone. Every guide includes practical tests and safety notes.
              </p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                <Link
                  to="/tools"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools",
                    guide_type: "calculator_hub",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Browse all free geology calculators <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/rock-and-mineral-identification"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/rock-and-mineral-identification",
                    guide_type: "workflow",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Follow the complete field workflow <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/rock-and-mineral-identification-chart"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/rock-and-mineral-identification-chart",
                    guide_type: "chart",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Open the printable ID chart <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/mohs-hardness-scale"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/mohs-hardness-scale",
                    guide_type: "hardness",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Estimate Mohs hardness <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/mineral-identification-worksheet"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/mineral-identification-worksheet",
                    guide_type: "worksheet",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Print the ID worksheet <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/quiz/mineral-identification"
                  onClick={() => trackEvent("home_cta", {
                    cta: "/quiz/mineral-identification",
                    placement: "popular_guides",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Take the mineral ID quiz <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/crystal-identification"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/crystal-identification",
                    guide_type: "crystal_id",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Identify a crystal or gemstone <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/fossil-identification"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/fossil-identification",
                    guide_type: "Field guide",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Identify a possible fossil <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/geode-identification"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/geode-identification",
                    guide_type: "geode_id",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Is this a geode? <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/meteorite-identification"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/meteorite-identification",
                    guide_type: "meteorite_id",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Is this a meteorite? <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/rock-cycle"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/rock-cycle",
                    guide_type: "rock_cycle",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Explore the rock cycle <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/mineral-fluorescence-chart"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/mineral-fluorescence-chart",
                    guide_type: "fluorescence",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  What glows under UV? <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/rockhounding-for-beginners"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/rockhounding-for-beginners",
                    guide_type: "Field guide",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Plan your first rockhounding trip <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/ontario-rock-identification"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/ontario-rock-identification",
                    guide_type: "Field guide",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Identify a rock found in Ontario <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/rock-tumbling-for-beginners"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/rock-tumbling-for-beginners",
                    guide_type: "Field guide",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Start rock tumbling safely <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/specific-gravity-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/specific-gravity-calculator",
                    guide_type: "specific_gravity",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Calculate specific gravity <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/rock-mineral-specimen-label-maker"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/rock-mineral-specimen-label-maker",
                    guide_type: "printable",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Make specimen labels <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/rock-mineral-collection-inventory"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/rock-mineral-collection-inventory",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Inventory your collection <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/geologic-time-scale"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/geologic-time-scale",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Explore geologic time <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/strike-dip-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/strike-dip-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Calculate strike and dip <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/sediment-grain-size-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/sediment-grain-size-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Classify sediment grain size <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/soil-texture-triangle-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/soil-texture-triangle-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Classify soil texture <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/core-recovery-rqd-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/core-recovery-rqd-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Calculate core recovery and RQD <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/rock-weight-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/rock-weight-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Estimate rock weight <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/slope-gradient-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/slope-gradient-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Convert slope and gradient <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/latitude-longitude-coordinate-converter"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/latitude-longitude-coordinate-converter",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Convert latitude and longitude <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/map-scale-distance-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/map-scale-distance-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Convert map scale and distance <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/distance-between-coordinates-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/distance-between-coordinates-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Measure distance between coordinates <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/true-thickness-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/true-thickness-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Correct drill intercept to true thickness <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/utm-coordinate-converter"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/utm-coordinate-converter",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Convert latitude/longitude and UTM <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/ore-grade-unit-converter"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/ore-grade-unit-converter",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Convert ore grade and assay units <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/contained-metal-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/contained-metal-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Calculate contained metal <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/weighted-average-drill-intercept-grade-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/weighted-average-drill-intercept-grade-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Weight drill intercept assay grades <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/drill-hole-desurvey-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/drill-hole-desurvey-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Desurvey a drill-hole trajectory <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/tools/earthquake-magnitude-energy-calculator"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/tools/earthquake-magnitude-energy-calculator",
                    guide_type: "tool",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Compare earthquake magnitude and energy <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/how-to-clean-rocks-and-minerals"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/how-to-clean-rocks-and-minerals",
                    guide_type: "Field guide",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Clean specimens without damage <ChevronRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/guides/is-my-rock-valuable"
                  onClick={() => trackEvent("home_guide_open", {
                    path: "/guides/is-my-rock-valuable",
                    guide_type: "Field guide",
                  })}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Check whether a rock may be valuable <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button asChild>
                <Link
                  to="/guides"
                  onClick={() => trackEvent("home_guide_open", { path: "/guides", guide_type: "hub" })}
                >
                  Browse all guides <ChevronRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="gap-2">
                <Link
                  to="/compare"
                  onClick={() => trackEvent("home_cta", { cta: "/compare", placement: "popular_guides" })}
                >
                  Comparisons <ChevronRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {popularGuides.map((guide) => (
              <Link
                key={guide.href}
                to={guide.href}
                onClick={() => trackEvent("home_guide_open", { path: guide.href, guide_type: guide.type })}
                className="group rounded-xl border bg-card p-4 transition-colors hover:border-accent/60 hover:bg-accent/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {guide.type}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-accent" />
                </div>
                <h3 className="font-display font-semibold group-hover:text-accent">{guide.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{guide.description}</p>
              </Link>
            ))}
          </div>

          <div className="mt-6 rounded-xl bg-secondary/40 p-4 text-center sm:flex sm:items-center sm:justify-between sm:text-left">
            <div>
              <p className="font-semibold">Not sure what to compare?</p>
              <p className="text-sm text-muted-foreground">Enter colour, lustre, hardness, texture, and other observations—no photo needed.</p>
            </div>
            <Button asChild className="mt-3 gap-2 sm:mt-0 sm:ml-4">
              <Link
                to="/quick-id"
                onClick={() => trackEvent("home_cta", { cta: "/quick-id", placement: "popular_guides" })}
              >
                Open Quick ID <Gem className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* For Geologists Band */}
      <section className="container py-8 md:py-12">
        <div className="rounded-2xl border bg-gradient-to-br from-primary/5 via-card to-accent/5 p-6 md:p-8">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold">
                <HardHat className="h-3.5 w-3.5" />
                FOR WORKING GEOLOGISTS
              </div>
              <h2 className="text-xl md:text-2xl font-display font-bold">
                Designed for real geological workflows.
              </h2>
              <p className="text-sm md:text-base text-muted-foreground max-w-2xl">
                Drill core logging assist, thin section petrography, alteration
                assemblages, vein generations, RQD prompts, and deposit-model
                context. Built for exploration projects, university labs, and core shacks.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                {["Porphyry-aware", "VMS / IOCG", "PPL / XPL", "AcQuire-style codes", "QA/QC ready"].map((tag) => (
                  <span key={tag} className="text-xs px-2 py-1 rounded-md bg-muted text-muted-foreground border">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <Button asChild size="lg" className="gap-2 w-full md:w-auto md:self-end">
              <Link
                to="/for-geologists"
                onClick={() => trackEvent("home_cta", { cta: "/for-geologists", placement: "geologist_band" })}
              >
                See the pro workflows <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* 3. Audience Cards */}
      <DeferredHomeSection>
        <AudienceCards stats={platformStats} />

        <SectionDivider />

        {/* 4. Complete Feature Grid */}
        <FeatureGrid stats={platformStats} />
      </DeferredHomeSection>

      <SectionDivider />

      {/* 5. How Rock ID Works */}
      <section className="bg-secondary/30 py-12 md:py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-3">
              How Rock ID Works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Get evidence-based identification candidates in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: "1", title: "Upload Photos", description: "Take 1-5 photos of your specimen. Close-up, mid-shot, and add a coin for scale." },
              { step: "2", title: "Answer Questions", description: "Optional questions help improve accuracy — skip any you don't know." },
              { step: "3", title: "Get Results", description: "Receive top 3 matches with confidence levels, explanations, and next steps." },
            ].map((item) => (
              <div
                key={item.step}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-full bg-accent text-accent-foreground text-xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-display font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="gap-2 touch-button">
              <Link
                to="/identify"
                onClick={() => trackEvent("home_cta", { cta: "/identify", placement: "how_it_works" })}
              >
                Try It Now
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <SectionDivider />

      <DeferredHomeSection>
        {/* 6. Pro Tools Deep Dive */}
        <ProToolsDeepDive />

        <SectionDivider />

        {/* 7. Training & Careers */}
        <TrainingCareers stats={platformStats} />
      </DeferredHomeSection>

      <SectionDivider />

      {/* 8. Built for Teams */}
      <section className="container py-12 md:py-20">
        <div className="rounded-2xl border bg-card p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium">
                <Building className="h-4 w-4" />
                Built for Teams
              </div>
              <h2 className="text-2xl md:text-3xl font-display font-bold">
                GeoMiner for Universities, Mining Companies & K-12
              </h2>
              <p className="text-muted-foreground">
                Standardize field tools, train your team with structured courses,
                inspire students about mining careers, and issue verifiable certificates — all on one platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="gap-2 w-full sm:w-auto">
                  <Link
                    to="/enterprise"
                    onClick={() => trackEvent("home_cta", { cta: "/enterprise", placement: "teams" })}
                  >
                    See enterprise features <ChevronRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="gap-2 w-full sm:w-auto">
                  <Link
                    to="/contact"
                    onClick={() => trackEvent("home_cta", { cta: "/contact", placement: "teams" })}
                  >
                    Discuss a Free Pilot
                  </Link>
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Users, label: "Team Management", desc: "Invite & manage members" },
                { icon: GraduationCap, label: "Training Tracks", desc: trainingTrackProof },
                { icon: Shield, label: "Certificates", desc: "Publicly verifiable" },
                { icon: Pickaxe, label: "Pro Tools", desc: "Core logging & more" },
                { icon: HardHat, label: "Careers", desc: careerProof },
                { icon: BarChart3, label: "Partner Reporting", desc: "Engagement analytics" },
              ].map((item) => (
                <div key={item.label} className="rounded-xl bg-secondary/50 p-4 text-center">
                  <item.icon className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <p className="font-medium text-sm">{item.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <SectionDivider />

      {/* 10. Final CTA */}
      <section className="container py-12 md:py-20">
        <div className="relative overflow-hidden rounded-2xl hero-gradient p-8 md:p-12 text-center text-white">
          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <h2 className="text-2xl md:text-3xl font-display font-bold">
              Ready to Become a Better Geologist?
            </h2>
            <p className="text-white/80">
              Identify a specimen, work through a free lesson, or build practical
              field skills at your own pace.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto touch-button">
                <Link
                  to={user ? "/my-finds" : "/identify"}
                  onClick={() => trackEvent("home_cta", { cta: user ? "/my-finds" : "/identify", placement: "final" })}
                >
                  {user ? "Open My Finds" : "Try Photo ID Free"}
                </Link>
              </Button>
              <Button asChild size="lg" variant="accent" className="w-full sm:w-auto touch-button">
                <Link
                  to={user ? "/training" : "/quick-id"}
                  onClick={() => trackEvent("home_cta", { cta: user ? "/training" : "/quick-id", placement: "final" })}
                >
                  {user ? "Browse Training" : "Quick ID — No Account"}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      </div>
    </div>
  );
}
